import React, { useState, useEffect } from 'react';
import { X, Settings } from 'lucide-react';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentProps {
  onAccept: (settings: CookieSettings) => void;
  onReject: () => void;
}

export function CookieConsent({ onAccept, onReject }: CookieConsentProps) {
  const [showBanner, setShowBanner] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true, // Zawsze włączone
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      setShowBanner(false);
      if (consent !== 'rejected') {
        const savedSettings = JSON.parse(consent);
        setSettings(savedSettings);
      }
    }
  }, []);

  const handleAccept = () => {
    onAccept(settings);
    localStorage.setItem('cookieConsent', JSON.stringify(settings));
    setShowBanner(false);
  };

  const handleReject = () => {
    const minimalSettings = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    onReject();
    setSettings(minimalSettings);
    localStorage.setItem('cookieConsent', JSON.stringify(minimalSettings));
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-50">
        <div className="max-w-7xl mx-auto p-4">
          {!showSettings ? (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600 space-y-2">
                <p>
                  Ta strona używa plików cookie, aby zapewnić najlepsze wrażenia z korzystania z naszej witryny.
                </p>
                <p className="text-xs">
                  Dbamy o prywatność dzieci zgodnie z RODO. Szczegółowe informacje znajdziesz w naszej{' '}
                  <button
                    onClick={() => setShowPrivacyPolicy(true)}
                    className="text-blue-500 hover:underline"
                  >
                    polityce prywatności
                  </button>.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Ustawienia
                </button>
                <button
                  onClick={handleReject}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Tylko niezbędne
                </button>
                <button
                  onClick={handleAccept}
                  className="px-4 py-2 text-sm text-white bg-blue-500 rounded-md hover:bg-blue-600 transition-colors"
                >
                  Akceptuj wszystkie
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Ustawienia plików cookie</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Niezbędne</p>
                    <p className="text-sm text-gray-500">
                      Te pliki cookie są wymagane do działania podstawowych funkcji witryny.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.necessary}
                    disabled
                    className="h-4 w-4 text-blue-500 rounded border-gray-300"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Analityczne (Google Analytics)</p>
                    <p className="text-sm text-gray-500">
                      Pomagają nam zrozumieć, jak użytkownicy korzystają z witryny. Dane są anonimizowane.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.analytics}
                    onChange={(e) => setSettings({ ...settings, analytics: e.target.checked })}
                    className="h-4 w-4 text-blue-500 rounded border-gray-300"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Marketingowe</p>
                    <p className="text-sm text-gray-500">
                      Używane do wyświetlania spersonalizowanych reklam.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.marketing}
                    onChange={(e) => setSettings({ ...settings, marketing: e.target.checked })}
                    className="h-4 w-4 text-blue-500 rounded border-gray-300"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  onClick={handleReject}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Tylko niezbędne
                </button>
                <button
                  onClick={handleAccept}
                  className="px-4 py-2 text-sm text-white bg-blue-500 rounded-md hover:bg-blue-600 transition-colors"
                >
                  Zapisz ustawienia
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <PrivacyPolicyModal
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />
    </>
  );
}